import { Form, Formik, useFormikContext } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { getDateFromYearAndMonth } from '../../../lib/Dates'
import { Budget, SheetCategory, Spend, SpendCreate } from '../../../lib/Types'
import { isCurrency } from '../../../lib/Validations'
import Headline from '../../Elements/Headline'
import { Modal } from '../../Elements/Modal'
import Button from '../../Formik/Button'
import Input from '../../Formik/Input'
import Select from '../../Formik/Select'
import Textarea from '../../Formik/Textarea'
import { SpendFrequency } from '../lib/Enums/SpendFrequency'
import { SpendType } from '../lib/Enums/SpendType'
import { useBudget } from '../lib/Hooks/Budget'
import { useCategories } from '../lib/Hooks/Categories'
import { useAddSpend } from '../lib/Hooks/Spends'

interface Props {
  parent?: Spend
  toggleHide: () => void
  show: boolean
  category?: SheetCategory
  saved?: () => void
}

const AddSpend: FC<Props> = ({ parent, toggleHide, show, category, saved }) => {
  const { t } = useTranslation(['budget', 'common'])
  const { data: budget } = useBudget()
  const { data: categories } = useCategories(budget!)
  const { mutate: addSpend } = useAddSpend(budget!)

  const save = async (spend: SpendCreate) => {
    // If we are adding a new spend to a parent spend, make sure to set the reference.
    if (parent) {
      spend.parent_id = parent.id
    }

    addSpend(spend)

    toggleHide()

    if (saved) saved()
  }

  const title = () => {
    if (parent) {
      return t('budget:addSpend.titleWithParent', { title: parent.title })
    } else if (category) {
      return t('budget:addSpend.titleWithCategory', { name: category.name })
    }

    return t('budget:addSpend.title')
  }

  const getDefaultCategory = () => {
    if (parent) {
      return parent.category.id
    } else if (category) {
      return category.id
    }
    return categories!.at(0)?.id.toString()
  }

  return (
    <Modal show={show} onHide={toggleHide}>
      {(closeModal: any) => (
        <>
          <Headline className='mb-4'>{title()}</Headline>
          <Formik
            initialValues={{
              title: '',
              description: '',
              category: getDefaultCategory(),
              frequency: SpendFrequency.MONTHLY,
              amount: 0,
              payment_month: '1',
            }}
            validationSchema={Yup.object({
              title: Yup.string().required(t('common:validation.required')),
              category: Yup.string().required(
                t('common:validation.required'),
              ),
              frequency: Yup.string().required(
                t('common:validation.required'),
              ),
              amount: Yup.string()
                .transform((currentValue) => {
                  // First remove all danish thousand seperators (.) and then replace the danish delimiter seperator (,) with the english one (.).
                  // This way it's possible to save floats.
                  return currentValue.replaceAll('.', '').replaceAll(',', '.')
                })
                .required(t('common:validation.required'))
                .test(isCurrency),
              payment_month: Yup.string().required(
                t('common:validation.required'),
              ),
            })}
            onSubmit={(values) => {
              const { frequency, amount, ...rest } = values

              const newSpend = {
                ...rest,
                type: SpendType.EXPENSE,
                accounting_date: getDateFromYearAndMonth(
                  new Date().getFullYear(),
                  parseInt(values.payment_month, 10),
                ),
                category: categories!.find(
                  (category) =>
                    category.id === parseInt(values.category as string, 10),
                ) as SheetCategory,
                amounts: [
                  {
                    formula: amount.toString(),
                    frequency,
                  },
                ],
              }
              save(newSpend)
              closeModal()
            }}
          >
            <AddSpendForm
              budget={budget!}
              parent={parent}
              category={category}
            />
          </Formik>
        </>
      )}
    </Modal>
  )
}

interface AddSpendFormProps {
  budget: Budget
  parent?: Spend
  category?: SheetCategory
}

const AddSpendForm: FC<AddSpendFormProps> = ({ budget, parent, category }) => {
  const { t } = useTranslation(['budget'])
  const { data: categories } = useCategories(budget)
  const {
    values: { frequency },
  } = useFormikContext<any>()

  return (
    <Form>
      <>
        <p className='mb-2'>{t('budget:addSpend.name')}</p>
        <Input
          name='title'
          placeholder={t('budget:addSpend.namePlaceholder')}
          className='mb-4'
        />
        <p className='mb-2'>{t('budget:shared.note')}</p>
        <Textarea
          name='description'
          placeholder={t('budget:addSpend.notePlaceholder')}
          className='mb-4'
        />
      </>
      {!parent && !category && (
        <>
          <p className='mb-2'>{t('budget:shared.categoryLabel')}</p>
          <Select name='category' className='mb-4'>
            {categories!.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </>
      )}
      <p className='mb-2'>{t('budget:addSpend.frequencyLabel')}</p>
      <Select name='frequency' className='mb-4'>
        <option value={SpendFrequency.MONTHLY}>
          {t('budget:shared.frequency.monthly')}
        </option>
        <option value={SpendFrequency.BIMONTHLY}>
          {t('budget:shared.frequency.bimonthly')}
        </option>
        <option value={SpendFrequency.QUARTERLY}>
          {t('budget:shared.frequency.quarterly')}
        </option>
        <option value={SpendFrequency.BIYEARLY}>
          {t('budget:shared.frequency.biyearly')}
        </option>
        <option value={SpendFrequency.YEARLY}>
          {t('budget:shared.frequency.yearly')}
        </option>
      </Select>
      {frequency === SpendFrequency.YEARLY && (
        <>
          <p className='mb-2'>{t('budget:shared.paymentMonthLabel')}</p>
          <Select name='payment_month' className='mb-4'>
            <option value='1'>{t('budget:shared.months.1')}</option>
            <option value='2'>{t('budget:shared.months.2')}</option>
            <option value='3'>{t('budget:shared.months.3')}</option>
            <option value='4'>{t('budget:shared.months.4')}</option>
            <option value='5'>{t('budget:shared.months.5')}</option>
            <option value='6'>{t('budget:shared.months.6')}</option>
            <option value='7'>{t('budget:shared.months.7')}</option>
            <option value='8'>{t('budget:shared.months.8')}</option>
            <option value='9'>{t('budget:shared.months.9')}</option>
            <option value='10'>{t('budget:shared.months.10')}</option>
            <option value='11'>{t('budget:shared.months.11')}</option>
            <option value='12'>{t('budget:shared.months.12')}</option>
          </Select>
        </>
      )}
      {frequency === SpendFrequency.BIYEARLY && (
        <>
          <p className='mb-2'>{t('budget:shared.paymentMonthsLabel')}</p>
          <Select name='payment_month' className='mb-4'>
            <option value='1'>{t('budget:shared.biyearlyMonths.1')}</option>
            <option value='2'>{t('budget:shared.biyearlyMonths.2')}</option>
            <option value='3'>{t('budget:shared.biyearlyMonths.3')}</option>
            <option value='4'>{t('budget:shared.biyearlyMonths.4')}</option>
            <option value='5'>{t('budget:shared.biyearlyMonths.5')}</option>
            <option value='6'>{t('budget:shared.biyearlyMonths.6')}</option>
          </Select>
        </>
      )}
      {frequency === SpendFrequency.QUARTERLY && (
        <>
          <p className='mb-2'>{t('budget:shared.paymentMonthsLabel')}</p>
          <Select name='payment_month' className='mb-4'>
            <option value='1'>{t('budget:shared.quarterlyMonths.1')}</option>
            <option value='2'>{t('budget:shared.quarterlyMonths.2')}</option>
            <option value='3'>{t('budget:shared.quarterlyMonths.3')}</option>
          </Select>
        </>
      )}
      {frequency === SpendFrequency.BIMONTHLY && (
        <>
          <p className='mb-2'>{t('budget:shared.paymentMonthsLabel')}</p>
          <Select name='payment_month' className='mb-4'>
            <option value='1'>{t('budget:shared.bimonthlyMonths.1')}</option>
            <option value='2'>{t('budget:shared.bimonthlyMonths.2')}</option>
          </Select>
        </>
      )}
      <p className='mb-2'>{t('budget:addSpend.amountLabel')}</p>
      <Input
        type='number'
        name='amount'
        placeholder={t('budget:shared.amountPlaceholder')}
        className='mb-4'
      />
      <Button type='submit'>{t('budget:shared.add')}</Button>
    </Form>
  )
}

export default AddSpend
